import * as React from "react";
import { NumberField as BaseNumberField } from "@base-ui/react/number-field";
import { FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, OutlinedInput } from "~/common/mui";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useNumberFieldLogic, type BaseNumberFieldProps } from "./useNumberFieldLogic";
import { useTranslation } from "react-i18next";
import { FORMATTING_LOCALE_CODE } from "~/utils/constants";
import ErrorIcon from "@mui/icons-material/Error";
/**
 * This component is a placeholder for FormControl to correctly set the shrink label state on SSR.
 */
function SSRInitialFilled(_: unknown): null {
  return null;
}
SSRInitialFilled.muiName = "Input";

export interface NumberFieldProps extends BaseNumberFieldProps {
  hideButtons?: boolean;
}

const NumberField = ({
  id: idProp,
  label,
  error,
  size = "medium",
  helperText,
  step = 1,
  onValueChange,
  disabled,
  hideButtons,
  roundToStep = true,
  showErrorIcon = false,
  ...other
}: NumberFieldProps) => {
  const { t } = useTranslation();

  const { handleValueChange } = useNumberFieldLogic({
    step,
    min: other.min,
    max: other.max,
    disabled,
    onValueChange,
  });
  let id = React.useId();
  if (idProp) {
    id = idProp;
  }

  return (
    <BaseNumberField.Root
      {...other}
      {...(!roundToStep && { step })}
      disabled={disabled}
      locale={FORMATTING_LOCALE_CODE}
      onValueChange={roundToStep ? handleValueChange : onValueChange}
      render={(props, state) => (
        <FormControl
          fullWidth
          ref={props.ref}
          disabled={state.disabled}
          required={state.required}
          error={error}
          variant="outlined"
        >
          {props.children}
        </FormControl>
      )}
    >
      <SSRInitialFilled {...other} />

      <InputLabel htmlFor={id} disabled={disabled} size={size}>
        {label}
      </InputLabel>

      <BaseNumberField.Input
        id={id}
        render={(props, state) => (
          <OutlinedInput
            label={label}
            inputRef={props.ref}
            value={state.inputValue}
            disabled={state.disabled}
            onBlur={(e) => {
              props.onBlur?.(e);
              other.onBlur?.();
            }}
            onChange={(e) => {
              props.onChange?.(e);
              const val = e.target.value;
              const num = val === "" ? null : parseInt(val, 10);
              onValueChange?.(num);
            }}
            onKeyUp={props.onKeyUp}
            onKeyDown={props.onKeyDown}
            onFocus={props.onFocus}
            size={size}
            slotProps={{
              input: props,
            }}
            endAdornment={
              !hideButtons ? (
                <InputAdornment
                  position="end"
                  sx={{
                    maxHeight: "unset",
                  }}
                >
                  <BaseNumberField.Decrement
                    render={<IconButton size={size} aria-label={t("common.input.quantity.decrement")} />}
                  >
                    <RemoveIcon fontSize={size} />
                  </BaseNumberField.Decrement>
                  <BaseNumberField.Increment
                    render={<IconButton size={size} aria-label={t("common.input.quantity.increment")} />}
                  >
                    <AddIcon fontSize={size} />
                  </BaseNumberField.Increment>
                </InputAdornment>
              ) : error && showErrorIcon ? (
                <ErrorIcon
                  color="error"
                  fontSize="medium"
                  sx={{
                    mr: 1,
                  }}
                />
              ) : null
            }
            sx={{ pr: 0 }}
          />
        )}
      />

      {helperText && <FormHelperText sx={{ mt: 0.5 }}>{helperText}</FormHelperText>}
    </BaseNumberField.Root>
  );
};

export default NumberField;
